import { FormikProps } from "formik"
import { Suspense, useRef } from "react"
import { useLocationState } from "../../../../../../core/router/routerHooks"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { UpdateAdmin } from "../../../../../users/userTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { createAdmin, getAdminDetails, GetAdminDetailsResponse, updateAdmin } from "../../../../../users/userApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"

import { useNavigate } from "react-router-dom"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { isNil } from "lodash"
import { getInvalidateQueryKey } from "../../../utils/getInvalidateQueryKey"
import { getUsersRoute } from "../../../utils/getUsersRoute"
import { createUserBody } from "../../../utils/createUserBody"
import { getPageTitle } from "../../../utils/getPageTitle"
import { getHomeRoute } from "../../../utils/getHomeRoutes"
import { selectEffectiveCompanyId, selectEffectiveUserGroup } from "../../../../../context/contextSelectors"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { CreateOrEditTenantAdminForm } from "../components/CreateOrEditTenantAdminForm"
import { TenantAdminQuery } from "../../../tenantAdminApi"
import {
    CreateEditTenantAdminFormValues,
    CreateOrEditTenantAdminProps,
    CreateOrUpdateAdminState,
} from "../tenantAdminTypes"
import { isSupportedUser } from "../helpers/isSupportedUser"

function transformAdminData(data: GetAdminDetailsResponse) {
    return data.admin
}

export function CreateOrEditTenantAdmin({ type: adminType }: CreateOrEditTenantAdminProps) {
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const userGroupId = useAppSelector(selectEffectiveUserGroup)

    const navigate = useNavigate()

    const { user: userItem } = useLocationState<CreateOrUpdateAdminState>({ user: undefined })

    const formRef = useRef<FormikProps<CreateEditTenantAdminFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    const mode = userItem ? "update" : "create"

    const { data: existingAdmin } = useApiQuery(getAdminDetails, {
        queryName: TenantAdminQuery.DETAILS_FOR_ADMIN,
        dependencies: {
            userId: userItem?.user.id ?? 0,
        },
        select: transformAdminData,
        enabled: Boolean(userItem),
    })

    const { mutateAsync: callCreateAdmin } = useApiMutation(createAdmin, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            if (isSupportedUser({ type: adminType })) {
                await context.queryClient.invalidateQueries({
                    queryKey: [getInvalidateQueryKey({ type: adminType })],
                })
            }

            navigate(getUsersRoute({ type: adminType, includeTab: true }))
        },
    })

    const { mutateAsync: callUpdateAdmin } = useApiMutation(updateAdmin, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, _, context) {
            if (isSupportedUser({ type: adminType })) {
                await context.queryClient.invalidateQueries({
                    queryKey: [getInvalidateQueryKey({ type: adminType })],
                })
                await context.queryClient.invalidateQueries({
                    queryKey: queryKeyFor(TenantAdminQuery.DETAILS_FOR_ADMIN, { userId: params.userId }),
                })
            }

            navigate(getUsersRoute({ type: adminType, includeTab: true }))
        },
    })

    async function handleSubmit(values: CreateEditTenantAdminFormValues) {
        if (isNil(companyId) || isNil(userGroupId)) return

        const admin = createUserBody(
            { type: adminType },
            values,
            {
                companyId,
                userGroupId,
            },
            existingAdmin,
        )

        if (existingAdmin) {
            const updateAdmin: UpdateAdmin = {
                ...admin,
                user: {
                    ...admin.user,
                    isActive: values.isActive,
                },
            }

            await callUpdateAdmin({
                userId: existingAdmin.user.id,
                admin: updateAdmin,
            })
        } else {
            await callCreateAdmin({
                admin,
            })
        }
    }

    return (
        <>
            <LocalizedStrict
                id={
                    mode === "create"
                        ? "office-manager-create-admin-form-title"
                        : "office-manager-edit-admin-form-title"
                }
                attrs={{ title: true }}
            >
                <PageHeader
                    page={getPageTitle({ type: adminType })}
                    title={mode === "create" ? "Create administrator" : "Edit administrator"}
                    rightElement={
                        <LocalizedStrict
                            id={mode === "create" ? "office-manager-create-user-form" : "office-manager-edit-user-form"}
                        >
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                {mode === "create" ? "Create" : "Save"}
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="office-manager-create-user-home-breadcrumb">
                            <BreadcrumbLink to={getHomeRoute({ type: adminType })}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="office-manager-create-user-users-breadcrumb">
                            <BreadcrumbLink to={getUsersRoute({ type: adminType })}>Account</BreadcrumbLink>
                        </LocalizedStrict>
                        {mode === "create" ? (
                            <LocalizedStrict id="office-manager-create-administrator-breadcrumb">
                                <Breadcrumb>Create administrator</Breadcrumb>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="office-manager-edit-administrator-breadcrumb">
                                <Breadcrumb>Edit administrator</Breadcrumb>
                            </LocalizedStrict>
                        )}
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <CreateOrEditTenantAdminForm
                        ref={formRef}
                        adminType={adminType}
                        existingAdmin={existingAdmin}
                        onSubmit={handleSubmit}
                    />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
